import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { usePricing } from '../hooks/usePricing';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { Plus, MapPin, MessageSquare, ClipboardList } from 'lucide-react';

const STATUS_BADGE = {
  open: { labelKey: 'services.reqStatusOpen', className: 'bg-green-50 text-green-700 border-green-200' },
  picked: { labelKey: 'services.reqStatusPicked', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  closed: { labelKey: 'services.reqStatusClosed', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  expired: { labelKey: 'services.reqStatusExpired', className: 'bg-amber-50 text-amber-700 border-amber-200' },
};

/**
 * Client dashboard "My requests" section. Fetches GET /api/service-requests/mine and lists
 * request cards with a status chip + response_count badge, each linking to the detail page.
 * A "+ Post a request" CTA routes to /services/requests/new.
 *
 * Renders null when the flag (pricing.services_enabled) is off.
 */
const MyServiceRequestsSection = ({ profile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pricing } = usePricing();
  const servicesEnabled = Boolean(pricing?.services_enabled);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMine = useCallback(async () => {
    if (!profile?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { setLoading(false); return; }
      const res = await api.get('/api/service-requests/mine', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setRequests(res.data?.requests || []);
    } catch (error) {
      console.error('[MyServiceRequestsSection] fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    // Flag-off (incl. while pricing resolves): leave loading untouched — the flag-off render
    // path returns null; flipping loading here caused a false empty-state flash on flag flip.
    if (servicesEnabled) {
      fetchMine();
    }
  }, [servicesEnabled, fetchMine]);

  // Flag off → render nothing.
  if (!servicesEnabled) return null;

  if (loading) {
    return (
      <div>
        <h2 className="font-heading text-2xl font-semibold mb-4">{t('services.reqMineTitle')}</h2>
        <Card><Skeleton className="h-24" /></Card>
      </div>
    );
  }

  return (
    <div data-testid="my-service-requests-section">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-2xl font-semibold">
          {t('services.reqMineTitle')}{requests.length > 0 ? ` (${requests.length})` : ''}
        </h2>
        <Button size="sm" variant="outline" onClick={() => navigate('/services/requests/new')}>
          <Plus size={16} className="mr-1" aria-hidden="true" />
          {t('services.reqPostCta')}
        </Button>
      </div>

      {requests.length === 0 ? (
        <Card className="border-dashed p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <ClipboardList className="text-muted-foreground shrink-0" size={18} aria-hidden="true" />
            <p className="text-sm text-muted-foreground line-clamp-2">{t('services.reqMineEmpty')}</p>
          </div>
          <Button size="sm" className="shrink-0" onClick={() => navigate('/services/requests/new')}>
            <Plus size={16} className="mr-1" aria-hidden="true" />
            {t('services.reqPostCta')}
          </Button>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {requests.map((r) => {
            const badge = STATUS_BADGE[r.status] || STATUS_BADGE.open;
            const count = r.response_count || 0;
            return (
              <Link
                key={r.id}
                to={`/services/requests/${r.id}`}
                className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Card className="p-4 space-y-2 h-full active:scale-[0.99] tap-highlight">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium line-clamp-2" title={r.title}>{r.title}</p>
                    <Badge variant="outline" className={`shrink-0 ${badge.className}`}>{t(badge.labelKey)}</Badge>
                  </div>
                  {r.municipality && (
                    <p className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin size={12} aria-hidden="true" />{r.municipality}
                    </p>
                  )}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MessageSquare size={12} aria-hidden="true" />
                    {t('services.reqResponseCount', { count })}
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyServiceRequestsSection;
